import type { MailOrderShippingZone } from '../../../types/location'

const TAX_PATTERN = /^\d{1,3}(\.\d{1,2})?$/

/** Display a tax percentage, e.g. "13.000" -> "13%", "" -> "0%". */
export function formatTaxPercent(value: string | number | null | undefined): string {
  if (value == null || value === '') return '0%'
  const num = Number(value)
  if (Number.isNaN(num)) return `${value}%`
  return `${num}%`
}

/** Serialize a tax percentage as a 2-decimal string, e.g. "13" -> "13.00". */
export function toTaxDecimalString(value: string | number): string {
  const num = Number(value)
  return Number.isFinite(num) && num >= 0 ? num.toFixed(2) : '0.00'
}

/** Normalize a stored tax value for an input field ("13.000" -> "13.00"). */
export function toTaxInputValue(value: string | number | null | undefined): string {
  if (value == null || value === '') return ''
  const num = Number(value)
  return Number.isFinite(num) ? num.toFixed(2) : String(value)
}

/** Returns an error message, or null when the value is a valid 0–100 percentage with ≤2 decimals. */
export function validateTaxPercent(value: string): string | null {
  const trimmed = value.trim()
  if (trimmed === '') return 'Enter a percentage between 0 and 100'
  const num = Number(trimmed)
  if (!Number.isFinite(num) || num < 0 || num > 100) {
    return 'Enter a percentage between 0 and 100'
  }
  if (!TAX_PATTERN.test(trimmed)) return 'Up to 2 decimal places'
  return null
}

export interface EffectiveZoneTax {
  value: string
  inherited: boolean
}

/** Tax a zone actually charges: its override, else the location default. */
export function getEffectiveZoneTax(
  zone: MailOrderShippingZone,
  defaultTax: string,
): EffectiveZoneTax {
  const override = zone.shipping_tax_percentage
  if (override == null || override === '') {
    return { value: defaultTax, inherited: true }
  }
  return { value: String(override), inherited: false }
}

/** Stable identity for a zone — backend rejects duplicate country+state. */
export function zoneKey(zone: Pick<MailOrderShippingZone, 'country' | 'state'>): string {
  return `${zone.country}-${zone.state}`
}
